import axios from "axios";
import { toast } from "sonner";
import { setCached, getCached, setMeta, clearAll } from "./offlineDb";
import { clearSWCaches } from "./swRegister";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth token is delivered exclusively via httpOnly `access_token` cookie set by
// POST /api/auth/verify-otp. `withCredentials: true` ensures the browser sends
// it on every request. No JS-accessible token storage (XSS-safe).
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// 22 Sep 2026 · Offline-Lesespiegel + Sitzungslaufzeit (8 Tage, Sliding).
const SESSION_TTL_MS = 8 * 24 * 60 * 60 * 1000;
const SESSION_EXP_KEY = "wb.session_expiry";
function setSessionExpiry() {
  try { localStorage.setItem(SESSION_EXP_KEY, String(Date.now() + SESSION_TTL_MS)); } catch { /* ignore */ }
}
export function getSessionExpiry() {
  try { const v = localStorage.getItem(SESSION_EXP_KEY); return v ? parseInt(v, 10) : 0; } catch { return 0; }
}
export function clearSessionExpiry() {
  try { localStorage.removeItem(SESSION_EXP_KEY); } catch { /* ignore */ }
}
function cacheKey(config) {
  const url = String(config?.url || "");
  const params = config?.params ? JSON.stringify(config.params) : "";
  return params ? `${url}?${params}` : url;
}
async function wipeLocal() {
  try { await clearAll(); } catch { /* ignore */ }
  try { clearSWCaches(); } catch { /* ignore */ }
  clearSessionExpiry();
}

// Feb 2026 · 18 Sep 2026 · Kunden-Ansicht pro Tab: Der Impersonation-Token
// liegt im sessionStorage (Tab-Scope) und geht als Bearer-Header mit — der
// Admin-Cookie im selben Browser bleibt unangetastet.
const IMPERSONATION_KEY = "wb.impersonation_token";
export function getImpersonationToken() {
  try { return window.sessionStorage.getItem(IMPERSONATION_KEY) || null; } catch { return null; }
}
export function setImpersonationToken(token) {
  try {
    if (token) window.sessionStorage.setItem(IMPERSONATION_KEY, token);
    else window.sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch { /* privacy mode */ }
}
api.interceptors.request.use((config) => {
  const t = getImpersonationToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Feb 2026 · Global Read-only-Interceptor für Admin-Impersonation.
// -----------------------------------------------------------
// Wenn ein Admin die Concierge- oder Kunden-Ansicht impersoniert, dürfen
// keine schreibenden Requests durchgehen. AuthProvider registriert seinen
// Read-only-Getter via `setReadOnlyChecker`; der Interceptor prüft bei
// jedem Write, ob er blockieren muss. Whitelist: Auth-Endpoints (Logout
// muss auch in Impersonation funktionieren, damit man die Session
// beenden kann) und die Impersonations-Meta-Endpoints selbst.
//
// Feb 2026 · Directive „PIN-Gate für Fremd-Concierge-Bearbeitung": Wenn
// der Blockergrund die Concierge-Impersonation eines anderen Users ist,
// öffnen wir statt eines rein blockierenden Toasts einen PIN-Prompt.
// Der Prompt-Handler wird von einer Layout-Komponente via
// `setPinPromptRequester` registriert; er kriegt einen Callback, den er
// nach erfolgreicher PIN-Eingabe aufruft.
let readOnlyChecker = () => false;
let readOnlyReasonProvider = () => "impersonation";
let pinPromptRequester = null;
export function setReadOnlyChecker(fn) {
  readOnlyChecker = typeof fn === "function" ? fn : () => false;
}
export function setReadOnlyReasonProvider(fn) {
  readOnlyReasonProvider = typeof fn === "function" ? fn : () => "impersonation";
}
export function setPinPromptRequester(fn) {
  pinPromptRequester = typeof fn === "function" ? fn : null;
}

function isWhitelistedWrite(url) {
  const u = String(url || "");
  if (u.includes("/auth/logout")) return true;
  if (u.includes("/auth/consume-impersonation")) return true;
  if (u.includes("/impersonation-link")) return true;
  // PIN-Verifikation muss durchgehen — sonst gäbe es eine Endlos-Schleife.
  if (u.includes("/team/settings/edit-pin/verify")) return true;
  // Feb 2026 · 16 Sep 2026 · Directive „PIN-Logik für Admin korrigieren":
  // Der Fremd-Concierge-Unlock nutzt jetzt die persönliche Team-PIN des
  // Admins. Dieser Endpoint darf im Interceptor NIE geblockt werden,
  // sonst kann der Admin die Ansicht nicht freischalten.
  if (u.includes("/me/team-pin/verify")) return true;
  // UI-Präferenzen (Objekt-Auswahl, Sidebar, Filter) sind nutzer-eigene
  // Einstellungen — kein Kundendaten-Write. Backend blockt Kunden-
  // Impersonation ohnehin mit 403 (wird im Client still verworfen).
  if (u.includes("/me/ui-prefs")) return true;
  return false;
}

// Feb 2026 · 11 Sep 2026 · Hotfix Concierge-Checkliste-Bug.
// Wenn ein Write bereits `X-Admin-Pin` mitschickt, hat der Admin gerade
// eben interaktiv (`AdminChecklistPinDialog`) die 4-stellige PIN
// verifiziert — der Backend-PIN-Guard springt dann sowieso als zweite
// Sicherheitsebene ein. Ein zusätzliches Blockieren durch den Read-only-
// Interceptor führt sonst zu einem doppelten PIN-Prompt, in dem der
// erste Toggle-Request verlorengeht (User-Report: „PIN entsperrt, aber
// Änderung landet nicht in der DB").
function hasAdminPinHeader(config) {
  const h = config?.headers;
  if (!h) return false;
  // AxiosHeaders-Instanz oder plain-Object — beide Fälle abdecken.
  if (typeof h.get === "function") {
    const v = h.get("X-Admin-Pin") ?? h.get("x-admin-pin");
    return !!(v && String(v).trim());
  }
  const v = h["X-Admin-Pin"] ?? h["x-admin-pin"];
  return !!(v && String(v).trim());
}

api.interceptors.request.use((config) => {
  const method = String(config.method || "get").toLowerCase();
  const isWrite = ["post", "put", "patch", "delete"].includes(method);
  if (!isWrite) return config;
  // 22 Sep 2026 · Offline: schreibende Aktionen blockieren (kein Buchen, PIN,
  // Extras, Abwesenheiten, Uploads ohne Netz). Logout & Impersonations-Meta
  // sind whitelisted, damit man die Session immer beenden kann.
  if (typeof navigator !== "undefined" && navigator.onLine === false && !isWhitelistedWrite(config.url)) {
    try { toast.error("Diese Aktion benötigt eine Internetverbindung."); } catch { /* ignore */ }
    return Promise.reject({
      __wb_offline: true,
      response: { status: 503, data: { detail: "Diese Aktion benötigt eine Internetverbindung." } },
      message: "Offline — write blocked client-side.",
    });
  }
  if (!readOnlyChecker()) return config;
  if (isWhitelistedWrite(config.url)) return config;
  if (hasAdminPinHeader(config)) return config;
  const reason = readOnlyReasonProvider();
  if (reason === "other-concierge" && typeof pinPromptRequester === "function") {
    // PIN-Gate: statt „unwiderruflich schreibgeschützt" bekommt der Admin
    // einen Prompt, um seine 4-stellige Bearbeitungs-PIN einzugeben.
    try { pinPromptRequester(); } catch { /* ignore */ }
    try {
      toast.error("PIN eingeben, um die Fremd-Ansicht zu bearbeiten.");
    } catch { /* ignore */ }
  } else {
    try {
      toast.error("Diese Ansicht ist schreibgeschützt — zurück zu Admin wechseln, um Änderungen zu machen.");
    } catch { /* ignore */ }
  }
  return Promise.reject({
    __wb_readonly: true,
    response: {
      status: 403,
      data: { detail: "Ansicht ist schreibgeschützt (Impersonation aktiv)." },
    },
    message: "Read-only impersonation view — write blocked client-side.",
  });
});

// 22 Sep 2026 · Offline-Lesespiegel: erfolgreiche GETs in IndexedDB spiegeln
// + Offline-Fallback (Cache statt Fehler). /auth/me: Sitzungslaufzeit-Handling.
api.interceptors.response.use(
  (response) => {
    try {
      const cfg = response.config || {};
      const method = String(cfg.method || "get").toLowerCase();
      const url = String(cfg.url || "");
      if (method === "get" && !url.includes("/files/")) {
        setCached(cacheKey(cfg), response.data);
        const ts = Date.now();
        setMeta("lastSync", ts);
        try { window.dispatchEvent(new CustomEvent("wb:sync", { detail: { ts } })); } catch { /* ignore */ }
      }
      if (url.includes("/auth/me") || url.includes("/auth/verify-otp")) {
        setSessionExpiry();
      }
    } catch { /* ignore */ }
    return response;
  },
  async (error) => {
    const cfg = error?.config || {};
    const method = String(cfg.method || "get").toLowerCase();
    const url = String(cfg.url || "");
    const status = error?.response?.status;
    const offline = (typeof navigator !== "undefined" && navigator.onLine === false) || !error?.response;

    // Sitzung serverseitig beendet (abgelaufen / Admin-Zwangs-Logout) → online 401.
    if (url.includes("/auth/me") && status === 401) {
      await wipeLocal();
      return Promise.reject(error);
    }

    if (method === "get" && offline) {
      // /auth/me offline: bei abgelaufener Sitzung KEIN Cache-Inhalt zeigen.
      if (url.includes("/auth/me")) {
        const exp = getSessionExpiry();
        if (!exp || Date.now() > exp) {
          await wipeLocal();
          return Promise.reject(error);
        }
      }
      const cached = await getCached(cacheKey(cfg));
      if (cached !== undefined && cached !== null) {
        return {
          data: cached, status: 200, statusText: "OK (cache)",
          headers: {}, config: cfg, __wb_fromCache: true,
        };
      }
    }
    return Promise.reject(error);
  },
);

export function formatErr(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.message) return detail.message;
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * Build the URL to render a file (image, PDF, …) stored via `/api/files/{id}`.
 * Accepts either:
 *   - a bare file ID (preferred, new format)
 *   - a legacy relative path like `/files/{id}`
 *   - a fully-qualified http(s) URL (returned as-is)
 * Returns `""` for falsy input.
 */
export function fileUrl(idOrPath) {
  if (!idOrPath) return "";
  const s = String(idOrPath).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:") || s.startsWith("blob:")) return s;
  const bareId = s.startsWith("/files/") ? s.slice("/files/".length) : s.startsWith("/api/files/") ? s.slice("/api/files/".length) : s;
  const imp = getImpersonationToken();
  return imp ? `${API}/files/${bareId}?auth=${encodeURIComponent(imp)}` : `${API}/files/${bareId}`;
}

export default api;
